import { useEffect, useRef, useCallback } from 'preact/hooks';
import { signalRService } from '@/services/signalRService';
/**
 * Manages SignalR subscription for landing page generation streaming events.
 * Joins the `landingpage-{webId}` group and listens for `LandingPageGenerationEvent`.
 *
 * Callbacks are stored in refs so the effect only re-runs when webId changes,
 * preventing mid-generation unsubscribe/resubscribe from callback identity changes.
 */
export function useLandingPageSignalR({ webId, onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone, onThankYouGenerating, onThankYouResult, onGenerationFailed, onThankYouGenerationFailed, onAnalyzing, onRebuilding, onRebuilt, onThankYouRebuilding, onThankYouRebuilt, onRebuildError, onConversationalResponse, onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended, }) {
    const handlerRef = useRef(null);
    // Store callbacks in ref so the effect doesn't depend on them
    const callbacksRef = useRef(null);
    const callbacks = {
        onThinking, onContent, onToolUse, onToolResult, onResult, onError, onDone, onThankYouGenerating, onThankYouResult, onGenerationFailed, onThankYouGenerationFailed, onAnalyzing, onRebuilding, onRebuilt, onThankYouRebuilding, onThankYouRebuilt, onRebuildError, onConversationalResponse, onAssistantMessageAppended, onChatStateChanged, onUserMessageAppended,
    };
    callbacksRef.current = callbacks;
    const cleanup = useCallback(() => {
        const entry = handlerRef.current;
        if (entry) {
            signalRService.off('LandingPageGenerationEvent', entry.handler);
            signalRService.leaveGroup(entry.group);
            handlerRef.current = null;
        }
    }, []);
    useEffect(() => {
        if (!webId)
            return;
        const group = `landingpage-${webId}`;
        let cancelled = false;
        const handleEvent = (payload) => {
            const raw = payload;
            // Handle both PascalCase (C# default) and camelCase (JS SignalR default)
            const event = {
                Type: (raw.Type ?? raw.type),
                Content: (raw.Content ?? raw.content),
                Tool: (raw.Tool ?? raw.tool),
                InputTokens: (raw.InputTokens ?? raw.inputTokens),
                OutputTokens: (raw.OutputTokens ?? raw.outputTokens),
                ModelId: (raw.ModelId ?? raw.modelId),
                Phase: (raw.Phase ?? raw.phase),
                WillRebuild: (raw.WillRebuild ?? raw.willRebuild),
                TurnUserId: (raw.TurnUserId ?? raw.turnUserId),
                TurnUserName: (raw.TurnUserName ?? raw.turnUserName),
                RebuildMode: (raw.RebuildMode ?? raw.rebuildMode),
            };
            const callbacks = callbacksRef.current;
            switch (event.Type) {
                case 'thinking':
                    callbacks.onThinking(event.Content ?? '');
                    break;
                case 'content':
                    callbacks.onContent(event.Content ?? '');
                    break;
                case 'tool_use':
                    callbacks.onToolUse(event.Tool ?? '');
                    break;
                case 'tool_result':
                    callbacks.onToolResult(event.Tool ?? '');
                    break;
                case 'result':
                    callbacks.onResult(event);
                    break;
                case 'error':
                    callbacks.onError(event.Content ?? 'An error occurred during generation');
                    break;
                case 'done':
                    callbacks.onDone();
                    break;
                case 'thankYouGenerating':
                    callbacks.onThankYouGenerating?.();
                    break;
                case 'thankYouResult':
                    callbacks.onThankYouResult?.(event);
                    break;
                case 'generationFailed':
                    callbacks.onGenerationFailed?.(event.Content ?? 'Generation failed');
                    break;
                case 'thankYouGenerationFailed':
                    callbacks.onThankYouGenerationFailed?.(event.Content ?? 'Thank you page generation failed');
                    break;
                case 'analyzing':
                    callbacks.onAnalyzing?.();
                    break;
                case 'rebuilding':
                    callbacks.onRebuilding?.();
                    break;
                case 'rebuilt':
                    callbacks.onRebuilt?.();
                    break;
                case 'thankYouRebuilding':
                    callbacks.onThankYouRebuilding?.();
                    break;
                case 'thankYouRebuilt':
                    callbacks.onThankYouRebuilt?.();
                    break;
                case 'rebuildError':
                    callbacks.onRebuildError?.(event.Content ?? 'Rebuild failed');
                    break;
                case 'conversationalResponse':
                    callbacks.onConversationalResponse?.(event.Content ?? '', event.WillRebuild === true, event.ModelId ?? null, event.OutputTokens ?? null, event.RebuildMode);
                    break;
                case 'assistantMessageAppended':
                    callbacks.onAssistantMessageAppended?.(event.Content ?? '', event.ModelId ?? null, event.OutputTokens ?? null);
                    break;
                case 'token_usage':
                    // Sparse end-of-turn snap. The dashboard no longer drives a live
                    // counter from this; AgentCostRecorder persists the value to the
                    // chat message server-side, MessageTokenFooter reads it back from
                    // the message on the next session load / SignalR userMessageAppended.
                    break;
                case 'chatStateChanged':
                    callbacks.onChatStateChanged?.(event.Content ?? 'Idle', event.TurnUserId ?? null, event.TurnUserName ?? null);
                    break;
                case 'userMessageAppended':
                    callbacks.onUserMessageAppended?.(event.Content ?? '', event.TurnUserId ?? null, event.TurnUserName ?? null);
                    break;
            }
        };
        signalRService.joinGroup(group).then(() => {
            if (cancelled) {
                signalRService.leaveGroup(group);
                return;
            }
            signalRService.on('LandingPageGenerationEvent', handleEvent);
            handlerRef.current = { handler: handleEvent, group };
        }).catch((err) => {
            console.error('Failed to join landing page SignalR group:', err);
        });
        return () => {
            cancelled = true;
            cleanup();
        };
    }, [webId, cleanup]); // Only re-run when webId changes, NOT on callback changes
    return { cleanup };
}
